import styled from "@emotion/styled";
import type { FC, ReactNode } from "react";
import { Button, type ButtonProps } from "react-aria-components";

const LinkButtonStyled = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0;
  border: 0;
  margin: 0;
  outline: 0;
  background: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-decoration: underline;

  svg {
    display: block;
  }

  &[data-focus-visible="true"] {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  &[data-variant="pill"] {
    text-decoration: none;
    gap: 0.125rem;
    padding: 0.5rem 0.625rem;
    border-radius: 1.5rem;
    background: var(--color-cream-darker);
    color: var(--color-charcoal);
  }
`;

const LinkButtonLabel = styled.span`
  &[data-variant="pill"] {
    padding: 0 0.25rem;
    /* Body/Little2 */
    font-family: "STK Bureau", sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%;
    letter-spacing: -0.28px;
  }
`;

export interface LinkButtonProps extends Omit<ButtonProps, "children"> {
  /** "text" (default) is a plain underlined link; "pill" adds the background/radius treatment. */
  variant?: "text" | "pill";
  icon?: ReactNode;
  /** Placement of `icon` relative to `children`. Defaults to "end" (e.g. a trailing chevron). */
  iconPosition?: "start" | "end";
  children?: ReactNode;
}

export const LinkButton: FC<LinkButtonProps> = ({
  variant = "text",
  icon,
  iconPosition = "end",
  children,
  ...props
}) => (
  <LinkButtonStyled data-variant={variant} {...props}>
    {icon && iconPosition === "start" && icon}
    {children && (
      <LinkButtonLabel data-variant={variant}>{children}</LinkButtonLabel>
    )}
    {icon && iconPosition === "end" && icon}
  </LinkButtonStyled>
);
